import {
  useEffect,
  useRef,
  useState,
  type PointerEvent,
  type SyntheticEvent,
  type TouchEvent,
} from "react";
import { createPortal } from "react-dom";
import { Trans } from "react-i18next";
import * as Sentry from "@sentry/react";
import {
  hasChosen,
  setConsent,
  getConsent,
  getConsentPersistDiagnostics,
  subscribeConsent,
  hasAccountConsentSync,
  waitForAccountPersist,
  isGpcActive,
} from "@/lib/analytics/consent";
import {
  captureProductionPostHogBeacon,
  captureWebMarketingConsentDecision,
} from "@/lib/analytics/environment";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { hideIntercom, setLauncherVisibility } from "@/lib/intercom";

type Category = "analytics" | "marketing";
type ConsentChoice = "accept_all" | "reject_all" | "save";
type PersistedChoice = {
  choice: ConsentChoice;
  persist: ReturnType<typeof getConsentPersistDiagnostics>;
};

const PARTNER_COUNT = 8;

// Essential-only diagnostics for the consent banner itself (no PII). Sentry is
// initialised unconditionally at boot (not consent-gated), so these breadcrumbs
// are available even before a choice is made — exactly when the mobile
// reachability dead-end this component guards against would occur. Everything is
// wrapped so diagnostics can never throw into the render/interaction path.
function reportConsentDiag(
  name: string,
  extra?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  try {
    const ua = navigator.userAgent || "";
    // Coarse "is this an embedded in-app browser" hint — these partition web
    // storage and drive the recurring "banner won't dismiss" reports.
    // Task #8007 — extended with the Outlook mobile in-app browser
    // (Outlook-iOS / Outlook-Android / MSOutlook) and Samsung's browser
    // (SamsungBrowser — used by Samsung Mail's link opening), both common in
    // the host demographic and previously invisible in this diagnostic.
    const inAppWebview =
      /\b(FBAN|FBAV|Instagram|Line|Twitter|GSA|FB_IAB|; wv\))/i.test(ua) ||
      /\bMicromessenger\b/i.test(ua) ||
      /\b(Outlook-iOS|Outlook-Android|MSOutlook|SamsungBrowser)\b/i.test(ua);
    // iOS Mail always opens links in a WKWebView with a plain Mobile Safari UA
    // (no FBAN/Instagram/etc markers), so `inAppWebview` above is false. Detect
    // it separately: the UA matches the AppleWebKit pattern but carries NONE of
    // the known third-party IAB markers — that combination is a strong signal
    // for iOS Mail's restricted webview context.
    const likelyMailWebview =
      !inAppWebview &&
      /\(iPhone|iPad\).*AppleWebKit/i.test(ua) &&
      !/\b(FBAN|FBAV|Instagram|Line|Twitter|GSA|FB_IAB|Micromessenger|Outlook-iOS|Outlook-Android|MSOutlook|SamsungBrowser|; wv\))/i.test(
        ua,
      );
    // Task #6637 — signals whether `dvh` viewport units are supported by this
    // browser/WebView. Old Android WebViews (including Gmail's in-app browser)
    // silently ignore `dvh`, which pushes the modal footer off-screen. Logging
    // this here lets future Sentry breadcrumbs on "banner unreachable" reports
    // be correlated with the root cause (no `dvh` support) immediately.
    const dvhSupported =
      typeof CSS !== "undefined" &&
      typeof CSS.supports === "function" &&
      CSS.supports("height", "1dvh");
    Sentry.addBreadcrumb({
      category: "consent",
      level: "info",
      message: name,
      data: {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        visualViewportHeight: window.visualViewport?.height ?? null,
        standalone:
          window.matchMedia?.("(display-mode: standalone)")?.matches ?? false,
        touch: (navigator.maxTouchPoints ?? 0) > 0,
        inAppWebview,
        likelyMailWebview,
        dvhSupported,
        ...extra,
      },
    });
  } catch {
    /* diagnostics must never break the banner */
  }
}

export function ConsentBanner() {
  const { t } = useAppTranslation();
  // A mobile tap can produce pointerup, touchend and a synthetic click. Track
  // the terminal input across the dialog so one gesture runs one handler while
  // a later intentional pointer/touch/keyboard activation remains available.
  const lastTerminalActivation = useRef<{ at: number; input: string } | null>(
    null,
  );
  const touchStarts = useRef<
    Record<string, { x: number; y: number; moved: boolean }>
  >({});
  const pointerStart = useRef<{
    control: string;
    pointerId: number;
    x: number;
    y: number;
    moved: boolean;
  } | null>(null);
  const pointerCancelled = useRef(false);
  const suppressedTerminal = useRef(false);
  const choiceCommitted = useRef(false);
  const pendingClosedChoice = useRef<PersistedChoice | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showVendors, setShowVendors] = useState(false);
  const [choices, setChoices] = useState<{
    analytics: boolean;
    marketing: boolean;
  }>({
    analytics: true,
    marketing: true,
  });

  useEffect(() => {
    if (!hasChosen()) {
      setOpen(true);
      reportConsentDiag("consent_banner_shown", {
        trigger: "first_visit",
        intercomAvailable: typeof window.Intercom === "function",
      });
    } else {
      const c = getConsent();
      setChoices({ analytics: c.analytics, marketing: c.marketing });
    }
    const reopen = () => {
      const c = getConsent();
      choiceCommitted.current = false;
      pendingClosedChoice.current = null;
      setChoices({ analytics: c.analytics, marketing: c.marketing });
      setShowSettings(true);
      setOpen(true);
      reportConsentDiag("consent_banner_shown", {
        trigger: "reopen",
        intercomAvailable: typeof window.Intercom === "function",
      });
    };
    window.addEventListener("bac:open-consent", reopen);
    return () => window.removeEventListener("bac:open-consent", reopen);
  }, []);

  // Task #6095 — if an account-stored choice hydrates after login (storage-
  // blocked browsers have no local copy, so the banner opened), close the
  // banner: the user already chose on this account. User-initiated choices
  // also fire this listener, but they already close via the click handlers.
  // We deliberately don't auto-close while the settings panel is open so a
  // background sync can't yank the panel away mid-edit.
  useEffect(() => {
    return subscribeConsent((c) => {
      setChoices({ analytics: c.analytics, marketing: c.marketing });
      setOpen((wasOpen) => {
        if (wasOpen && !showSettings && hasChosen()) {
          // User choices close on the next task so a browser-generated click
          // cannot fall through to the page after a touchend unmounts us.
          if (choiceCommitted.current) return wasOpen;
          if (!choiceCommitted.current) {
            reportConsentDiag("consent_banner_closed", {
              trigger: "account_hydration",
            });
          }
          return false;
        }
        return wasOpen;
      });
    });
  }, [showSettings]);

  // Report closure only after React has committed the hidden state. This
  // distinguishes "the handler ran" from "the dialog actually closed".
  useEffect(() => {
    if (open || !pendingClosedChoice.current) return;
    const completed = pendingClosedChoice.current;
    pendingClosedChoice.current = null;
    reportConsentDiag("consent_banner_closed", {
      trigger: completed.choice,
    });
    reportConsentDiag("consent_persistence_outcome", {
      choice: completed.choice,
      persist: completed.persist,
    });
  }, [open]);

  // Task #6637 — while the consent banner is open, hide the Intercom floating
  // launcher. The launcher sits at z-index 2,147,483,647 and can intercept taps
  // on the Accept/Reject buttons on tablets and
  // desktop webviews. Mirrors the pattern used in ConversationPage and
  // MobileBookingBar. The cleanup restores the launcher to its previous state
  // so Intercom remains available immediately after the banner closes.
  useEffect(() => {
    if (!open) return;
    setLauncherVisibility(false);
    // Task #7488 — also close any open Messenger window. The Messenger widget
    // renders at z-index 2,147,483,647 and
    // intercepts pointer events even when the launcher bubble is hidden. We
    // only need to dismiss it on entry; Intercom manages its own re-open state
    // so we do not need a matching show() in the cleanup.
    hideIntercom();
    return () => {
      setLauncherVisibility(true);
    };
  }, [open]);

  // A native modal dialog enters the browser's top layer. Unlike z-index, the
  // top layer cannot be undercut by a body-level application or support-widget
  // overlay, and the rest of the document is inert while consent is pending.
  // For older webviews without showModal, explicitly neutralise every body
  // sibling and install a document-level guard so even a later max-z overlay
  // cannot win hit-testing. Preserve prior values so other modal systems are
  // not disturbed when consent closes.
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const siblingState = new Map<
      HTMLElement,
      {
        inert: boolean;
        ariaHidden: string | null;
        pointerEvents: string;
        visibility: string;
      }
    >();
    let usedNativeModal = false;

    if (typeof dialog.showModal === "function") {
      try {
        if (!dialog.open) dialog.showModal();
        usedNativeModal = true;
      } catch {
        if (!dialog.open) dialog.setAttribute("open", "");
      }
    } else {
      dialog.setAttribute("open", "");
    }

    const neutraliseSibling = (element: Element) => {
      if (!(element instanceof HTMLElement) || element === dialog) return;
      if (!siblingState.has(element)) {
        siblingState.set(element, {
          inert: element.inert,
          ariaHidden: element.getAttribute("aria-hidden"),
          pointerEvents: element.style.pointerEvents,
          visibility: element.style.visibility,
        });
      }
      element.inert = true;
      element.setAttribute("aria-hidden", "true");
      element.style.pointerEvents = "none";
      element.style.visibility = "hidden";
    };

    const enforceFallbackModal = () => {
      for (const child of Array.from(document.body.children)) {
        neutraliseSibling(child);
      }
    };

    let fallbackStyle: HTMLStyleElement | null = null;
    const blockOutsideInteraction = (event: Event) => {
      if (dialog.contains(event.target as Node)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    const blockedEvents = [
      "pointerdown",
      "pointerup",
      "touchstart",
      "touchend",
      "mousedown",
      "mouseup",
      "click",
    ] as const;
    if (!usedNativeModal) {
      enforceFallbackModal();
      fallbackStyle = document.createElement("style");
      fallbackStyle.dataset.consentFallbackGuard = "true";
      fallbackStyle.textContent = `
        body > :not(dialog[data-consent-modal-root="true"]) {
          pointer-events: none !important;
          visibility: hidden !important;
        }
        body > dialog[data-consent-modal-root="true"] {
          pointer-events: auto !important;
          visibility: visible !important;
        }
      `;
      document.head.appendChild(fallbackStyle);
      for (const eventName of blockedEvents) {
        document.addEventListener(
          eventName,
          blockOutsideInteraction,
          true,
        );
      }
    }

    const focusableSelector =
      'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
    const focusPreferredControl = () => {
      const preferred =
        dialog.querySelector<HTMLElement>('[data-testid="consent-accept"]') ??
        dialog.querySelector<HTMLElement>(focusableSelector);
      (preferred ?? dialog).focus();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter((element) => element.getClientRects().length > 0);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first ||
          !dialog.contains(document.activeElement))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last ||
          !dialog.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    const onFocusIn = (event: FocusEvent) => {
      if (!dialog.contains(event.target as Node)) focusPreferredControl();
    };
    dialog.addEventListener("keydown", onKeyDown);
    if (!usedNativeModal) document.addEventListener("focusin", onFocusIn);
    const focusTimer = window.setTimeout(focusPreferredControl, 0);

    return () => {
      window.clearTimeout(focusTimer);
      dialog.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
      for (const eventName of blockedEvents) {
        document.removeEventListener(
          eventName,
          blockOutsideInteraction,
          true,
        );
      }
      fallbackStyle?.remove();
      for (const [element, previous] of siblingState) {
        element.inert = previous.inert;
        if (previous.ariaHidden === null) {
          element.removeAttribute("aria-hidden");
        } else {
          element.setAttribute("aria-hidden", previous.ariaHidden);
        }
        element.style.pointerEvents = previous.pointerEvents;
        element.style.visibility = previous.visibility;
      }
      if (typeof dialog.close === "function" && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute("open");
      }
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  // Record the outcome of a choice, and — critically — surface the rare case
  // where NO durable layer accepted the write. That is the true "banner keeps
  // coming back" dead-end (in-app browsers that partition storage); the choice
  // still sticks for this session via the in-memory layer, but we want the
  // signal in Sentry so a recurrence is diagnosable rather than invisible.
  const recordChoicePersistence = (
    choice: ConsentChoice,
    diag: ReturnType<typeof getConsentPersistDiagnostics>,
  ) => {
    if (diag && !diag.localStorage && !diag.cookieReadback) {
      // Task #6095 — for logged-in users the account layer can still persist
      // the choice durably (the only store an all-blocking browser can't
      // touch). Await its outcome before deciding whether this is a real
      // durable-persistence dead-end worth a Sentry warning.
      void (async () => {
        if (hasAccountConsentSync() && (await waitForAccountPersist())) {
          reportConsentDiag("consent_persisted_via_account", { choice });
          return;
        }
        try {
          Sentry.captureMessage(
            "[consent] durable persistence unavailable — banner may re-show on reload",
            "warning",
          );
        } catch {
          /* never break the interaction */
        }
        // Task #8028 — fire a lightweight PostHog beacon so the rate of
        // durable-persistence failures can be tracked in a funnel without
        // relying solely on the Sentry issues list. The PostHog SDK is
        // consent-gated, so we post directly to the ingest proxy (the same
        // pattern as the pre-mount watchdog beacon in main.tsx). Best-effort:
        // never throws, never blocks the interaction.
        try {
          captureProductionPostHogBeacon("consent_persist_failed", {
            distinct_id: "consent-persist-watchdog",
            $process_person_profile: false,
            choice,
            sessionStorage: diag.sessionStorage,
          });
        } catch {
          /* telemetry must never affect the interaction */
        }
      })();
    }
  };

  // Task #6837 (G) — when Global Privacy Control is active, "Accept all"
  // grants analytics only; marketing stays opted out (setConsent enforces
  // this too, but we request it explicitly for clarity).
  const gpcActive = isGpcActive();
  const commitChoice = (
    choice: ConsentChoice,
    next: { analytics: boolean; marketing: boolean },
  ) => {
    if (choiceCommitted.current) return;
    choiceCommitted.current = true;
    const applied = setConsent(next);
    captureWebMarketingConsentDecision({
      choice,
      marketingGranted: applied.marketing,
      gpc: applied.gpc === true,
    });
    const persist = getConsentPersistDiagnostics();
    reportConsentDiag("consent_choice_applied", { choice });
    pendingClosedChoice.current = { choice, persist };
    recordChoicePersistence(choice, persist);
    window.setTimeout(() => setOpen(false), 0);
  };
  const acceptAll = () =>
    commitChoice("accept_all", {
      analytics: true,
      marketing: !gpcActive,
    });
  const rejectAll = () =>
    commitChoice("reject_all", { analytics: false, marketing: false });
  const saveChoices = () => commitChoice("save", choices);
  const toggle = (k: Category) => setChoices((s) => ({ ...s, [k]: !s[k] }));

  // The contract deliberately does not use `event.cancelable`: some affected
  // webviews deliver a non-cancelable terminal touch event and no click. The
  // native click path retains keyboard and mouse semantics; pointer/touch are
  // only a fallback for clients that fail to synthesize that click.
  const activate =
    (control: string, handler: () => void) => (event: SyntheticEvent) => {
      const now = Date.now();
      const previousTerminal = lastTerminalActivation.current;
      const isKeyboardLikeClick =
        event.type === "click" &&
        (event.nativeEvent as globalThis.MouseEvent).detail === 0;
      if (suppressedTerminal.current && !isKeyboardLikeClick) return;
      const isFollowUpClick =
        event.type === "click" &&
        !isKeyboardLikeClick &&
        previousTerminal &&
        previousTerminal.input !== "click" &&
        now - previousTerminal.at < 750;
      const isDuplicateTerminalInput =
        event.type !== "click" &&
        previousTerminal &&
        previousTerminal.input !== "click" &&
        previousTerminal.input !== event.type &&
        now - previousTerminal.at < 100;
      if (isFollowUpClick || isDuplicateTerminalInput) return;
      if (event.type !== "click") {
        lastTerminalActivation.current = { at: now, input: event.type };
        suppressedTerminal.current = false;
      }
      if (event.type === "touchend" && event.cancelable) {
        event.preventDefault();
      }
      reportConsentDiag("consent_activation_received", {
        control,
        input: event.type,
      });
      handler();
    };

  const suppressMovedGesture = (
    input: "pointerup" | "pointercancel" | "touchend" | "touchcancel",
  ) => {
    // A browser may still synthesize click after pointer/touch movement.
    // Record the terminal event without invoking the action so that click is
    // treated as part of the same (non-activating) physical gesture.
    lastTerminalActivation.current = { at: Date.now(), input };
    suppressedTerminal.current = true;
  };

  const activationProps = (control: string, handler: () => void) => ({
    onClick: activate(control, handler),
    onPointerDown: (event: PointerEvent) => {
      lastTerminalActivation.current = null;
      suppressedTerminal.current = false;
      pointerCancelled.current = false;
      pointerStart.current = {
        control,
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        moved: false,
      };
    },
    onPointerMove: (event: PointerEvent) => {
      const start = pointerStart.current;
      if (!start || start.pointerId !== event.pointerId) return;
      if (
        Math.abs(event.clientX - start.x) > 10 ||
        Math.abs(event.clientY - start.y) > 10
      ) {
        start.moved = true;
      }
    },
    onPointerUp: (event: PointerEvent) => {
      const start = pointerStart.current;
      pointerStart.current = null;
      // Pointer-up without a matching start cannot be proven to be a tap.
      // Let the native click or touch fallback handle that client instead.
      if (!start || start.pointerId !== event.pointerId) return;
      const moved =
        start.moved ||
        start.control !== control ||
        Math.abs(event.clientX - start.x) > 10 ||
        Math.abs(event.clientY - start.y) > 10;
      if (moved) {
        suppressMovedGesture("pointerup");
        return;
      }
      activate(control, handler)(event);
    },
    onPointerCancel: (event: PointerEvent) => {
      if (pointerStart.current?.pointerId === event.pointerId) {
        pointerStart.current = null;
        pointerCancelled.current = true;
        suppressMovedGesture("pointercancel");
      }
    },
    onTouchStart: (event: TouchEvent) => {
      pointerCancelled.current = false;
      lastTerminalActivation.current = null;
      suppressedTerminal.current = false;
      const touch = event.touches[0];
      if (!touch) return;
      touchStarts.current[control] = {
        x: touch.clientX,
        y: touch.clientY,
        moved: false,
      };
    },
    onTouchMove: (event: TouchEvent) => {
      const start = touchStarts.current[control];
      const touch = event.touches[0];
      if (!start || !touch) return;
      if (
        Math.abs(touch.clientX - start.x) > 10 ||
        Math.abs(touch.clientY - start.y) > 10
      ) {
        start.moved = true;
      }
    },
    onTouchEnd: (event: TouchEvent) => {
      const start = touchStarts.current[control];
      delete touchStarts.current[control];
      if (pointerCancelled.current) {
        pointerCancelled.current = false;
        suppressMovedGesture("touchend");
        return;
      }
      const touch = event.changedTouches[0];
      const moved =
        start?.moved ||
        (start != null &&
          touch != null &&
          (Math.abs(touch.clientX - start.x) > 10 ||
            Math.abs(touch.clientY - start.y) > 10));
      // Some webviews only deliver touchend. Treat it as activation without
      // consulting cancelable, but never turn the end of a scroll into consent.
      if (moved) {
        suppressMovedGesture("touchend");
        return;
      }
      activate(control, handler)(event);
    },
    onTouchCancel: () => {
      delete touchStarts.current[control];
      suppressMovedGesture("touchcancel");
    },
  });

  // Task #6637 — vh/dvh fallback for old Android WebViews (including Gmail's
  // in-app browser). These WebViews silently ignore `dvh` units, so
  // `min-h-[100dvh]` / `max-h-[calc(100dvh-2rem)]` are treated as invalid and
  // the card grows taller than the viewport, pushing Accept/Reject off-screen.
  // We detect support at render time via CSS.supports and serve `dvh` to
  // capable browsers, `vh` to everything else. Inline style takes precedence
  // over the Tailwind `min-h-full` class, which remains as a belt-and-braces
  // fallback for unusual environments where CSS.supports itself is absent.
  const dvhSupported =
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("height", "1dvh");
  const wrapperMinHeight = dvhSupported ? "100dvh" : "100vh";
  const cardMaxHeight = dvhSupported ? "calc(100dvh - 2rem)" : "calc(100vh - 2rem)";

  return createPortal(
    // Outer overlay is the SCROLL container. Making the whole overlay scrollable
    // (not just the card's inner text) guarantees the footer with the
    // Accept/Reject/Save buttons is always reachable even when a mobile browser's
    // dynamic address bar shrinks the visible viewport — the mobile dead-end this
    // task fixes. `overscroll-contain` stops the scroll from chaining to the page.
    <dialog
      ref={dialogRef}
      className="pointer-events-auto fixed inset-0 z-[2147483647] m-0 h-auto w-auto max-h-none max-w-none cursor-pointer touch-manipulation overflow-y-auto overscroll-contain border-0 bg-black/40 p-0"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-title"
      data-testid="consent-dialog"
      data-consent-modal-root="true"
      onCancel={(event) => event.preventDefault()}
    >
      {/* Centering wrapper sized to the viewport. `min-h-full` is the Tailwind
          class fallback; the inline style provides the `vh`/`dvh` value chosen
          for this browser (see dvhSupported above). Safe-area padding keeps the
          card clear of the notch / home indicator. When the card is taller than
          the viewport, this wrapper grows and the overlay above scrolls. */}
      <div
        className="flex min-h-full items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
        style={{ minHeight: wrapperMinHeight }}
      >
        <div
          className="flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          style={{ maxHeight: cardMaxHeight }}
        >
          {!showSettings ? (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-6 pt-8 sm:px-10 sm:pt-10">
                <h2
                  id="consent-title"
                  className="text-2xl font-bold tracking-tight text-foreground sm:text-[28px]"
                >
                  {t("consent.title")}
                </h2>
                <p className="mt-5 text-[15px] leading-relaxed text-foreground">
                  <Trans
                    i18nKey="consent.intro_paragraph"
                    values={{ partners: PARTNER_COUNT }}
                    components={{
                      strong: <span className="font-semibold" />,
                      accept: <span className="font-medium" />,
                      reject: <span className="font-medium" />,
                      settings: (
                        <button
                          type="button"
                          {...activationProps("more_settings_inline", () =>
                            setShowSettings(true),
                          )}
                          className="font-medium underline underline-offset-2 hover:text-brand"
                        />
                      ),
                      privacy: (
                        <a
                          href="/privacy"
                          className="font-medium underline underline-offset-2 hover:text-brand"
                        />
                      ),
                    }}
                  />
                </p>

                <h3 className="mt-7 text-[15px] font-bold text-foreground">
                  {t("consent.data_helps_heading")}
                </h3>
                <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">
                  {t("consent.data_helps_body")}
                </p>
                <button
                  type="button"
                  {...activationProps("vendors", () =>
                    setShowVendors((v) => !v),
                  )}
                  className="mt-3 text-[13px] font-semibold text-muted-foreground hover:text-foreground"
                >
                  {showVendors
                    ? t("consent.hide_vendors")
                    : t("consent.list_vendors")}
                </button>
                {showVendors && (
                  <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[13px] text-muted-foreground sm:grid-cols-3">
                    <li>{t("consent.vendors.google_analytics_4")}</li>
                    <li>{t("consent.vendors.google_ads")}</li>
                    <li>{t("consent.vendors.posthog")}</li>
                    <li>{t("consent.vendors.meta_pixel")}</li>
                    <li>{t("consent.vendors.stripe")}</li>
                    <li>{t("consent.vendors.cloudflare")}</li>
                    <li>{t("consent.vendors.sentry")}</li>
                    <li>{t("consent.vendors.intercom")}</li>
                  </ul>
                )}
                <p className="mt-4 text-[13px] leading-relaxed text-muted-foreground">
                  {t("consent.intercom_note")}
                </p>
                {gpcActive && (
                  <p className="mt-4 text-[13px] leading-relaxed text-muted-foreground">
                    {t("consent.gpc_note")}
                  </p>
                )}
              </div>

              <div className="flex shrink-0 flex-col-reverse items-stretch gap-3 border-t border-border px-8 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-10">
                <button
                  type="button"
                  {...activationProps("more_settings", () =>
                    setShowSettings(true),
                  )}
                  className="text-left text-[15px] font-semibold text-foreground underline underline-offset-2 hover:text-brand"
                  data-testid="consent-customise"
                >
                  {t("consent.more_settings")}
                </button>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    {...activationProps("reject_all", rejectAll)}
                    className="touch-manipulation cursor-pointer rounded-full border border-brand bg-transparent px-7 py-3 text-sm font-semibold text-brand transition hover:bg-brand-tint"
                    data-testid="consent-reject"
                  >
                    {t("consent.reject_all")}
                  </button>
                  <button
                    type="button"
                    {...activationProps("accept_all", acceptAll)}
                    className="touch-manipulation cursor-pointer rounded-full bg-brand px-7 py-3 text-sm font-semibold text-white transition hover:bg-brand-dark-ink"
                    data-testid="consent-accept"
                  >
                    {t("consent.accept")}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-6 pt-8 sm:px-10 sm:pt-10">
                <h2
                  id="consent-title"
                  className="text-2xl font-bold tracking-tight text-foreground"
                >
                  {t("consent.preferences_title")}
                </h2>
                <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">
                  <Trans
                    i18nKey="consent.manage_paragraph"
                    components={{
                      link: (
                        <a
                          href="/settings?section=privacy"
                          className="font-medium underline underline-offset-2 hover:text-brand"
                        />
                      ),
                    }}
                  />
                </p>
                <ul className="mt-5 space-y-3">
                  <li className="flex items-start gap-3 rounded-xl border border-border p-4">
                    <div
                      className="mt-1 h-4 w-4 rounded-sm border border-muted-foreground/40 bg-muted"
                      aria-hidden
                    />
                    <div className="flex-1 text-sm">
                      <div className="font-semibold">
                        {t("consent.essential")}
                      </div>
                      <div className="mt-1 text-muted-foreground">
                        {t("consent.essential_desc")}
                      </div>
                    </div>
                    <span className="text-xs font-medium uppercase text-muted-foreground">
                      {t("consent.always_on")}
                    </span>
                  </li>
                  {(["analytics", "marketing"] as Category[]).map((cat) => (
                    <li
                      key={cat}
                      className="flex items-start gap-3 rounded-xl border border-border p-4"
                    >
                      <input
                        type="checkbox"
                        checked={choices[cat]}
                        onChange={() => toggle(cat)}
                        className="mt-1 h-4 w-4 accent-brand"
                        aria-label={cat}
                        data-testid={`consent-toggle-${cat}`}
                      />
                      <div className="flex-1 text-sm">
                        <div className="font-semibold capitalize">{cat}</div>
                        <div className="mt-1 text-muted-foreground">
                          {cat === "analytics"
                            ? t("consent.cat_analytics_desc")
                            : t("consent.cat_marketing_desc")}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex shrink-0 flex-col-reverse items-stretch gap-3 border-t border-border px-8 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-10">
                <button
                  type="button"
                  {...activationProps("back", () => setShowSettings(false))}
                  className="text-left text-[15px] font-semibold text-foreground underline underline-offset-2 hover:text-brand"
                  data-testid="consent-back"
                >
                  {t("common.back")}
                </button>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    {...activationProps("reject_all_settings", rejectAll)}
                    className="touch-manipulation cursor-pointer rounded-full bg-brand px-7 py-3 text-sm font-semibold text-white transition hover:bg-brand-dark-ink"
                    data-testid="consent-reject-settings"
                  >
                    {t("consent.reject_all")}
                  </button>
                  <button
                    type="button"
                    {...activationProps("save_preferences", saveChoices)}
                    className="touch-manipulation cursor-pointer rounded-full bg-brand px-7 py-3 text-sm font-semibold text-white transition hover:bg-brand-dark-ink"
                    data-testid="consent-save"
                  >
                    {t("consent.save_preferences")}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </dialog>,
    document.body,
  );
}
